import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import complianceService from '../../../services/complianceService';

const CriticalAlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);
  const [newAlert, setNewAlert] = useState({
    alertType: 'license_expiry',
    severity: 'medium',
    title: '',
    message: '',
    dueDate: ''
  });

  const alertTypeOptions = [
    { value: 'license_expiry', label: 'License Expiry' },
    { value: 'inspection_due', label: 'Inspection Due' },
    { value: 'training_required', label: 'Training Required' },
    { value: 'document_missing', label: 'Document Missing' },
    { value: 'compliance_violation', label: 'Compliance Violation' },
    { value: 'audit_scheduled', label: 'Audit Scheduled' }
  ];

  const severityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await complianceService?.getAllAlerts();
      // Filter out resolved alerts for the critical panel
      setAlerts(data?.filter(alert => !alert?.isResolved) || []);
    } catch (err) {
      setError(err?.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bgColor: 'bg-error/10',
          borderColor: 'border-error',
          textColor: 'text-error',
          iconBg: 'bg-error',
          iconColor: 'text-white'
        };
      case 'high':
        return {
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning',
          textColor: 'text-warning',
          iconBg: 'bg-warning',
          iconColor: 'text-white'
        };
      case 'medium':
        return {
          bgColor: 'bg-accent/10',
          borderColor: 'border-accent',
          textColor: 'text-accent',
          iconBg: 'bg-accent',
          iconColor: 'text-white'
        };
      default:
        return {
          bgColor: 'bg-muted/10',
          borderColor: 'border-muted',
          textColor: 'text-muted-foreground',
          iconBg: 'bg-muted',
          iconColor: 'text-white'
        };
    }
  };

  const formatDaysUntil = (dateString) => {
    if (!dateString) return 'No due date';
    
    const now = new Date();
    const dueDate = new Date(dateString);
    const diffTime = dueDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    return `${diffDays} days`;
  };

  const handleDismissAlert = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await complianceService?.resolveAlert(alertId, 'Resolved from critical alerts panel');
      setAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
    } catch (err) {
      setError(err?.message || 'Failed to resolve alert');
    }
  };

  const handleCreateAlert = async (e) => {
    e?.preventDefault();
    try {
      setSendingSMS(true);
      const createdAlert = await complianceService?.createAlert({
        ...newAlert,
        dueDate: newAlert?.dueDate ? new Date(newAlert.dueDate)?.toISOString() : null
      });
      
      setAlerts(prev => [createdAlert, ...prev]);
      setNewAlert({
        alertType: 'license_expiry',
        severity: 'medium',
        title: '',
        message: '',
        dueDate: ''
      });
      setShowAddForm(false);
      
      // Show SMS notification for critical/high alerts
      if (newAlert?.severity === 'critical' || newAlert?.severity === 'high') {
        setError('Alert created and urgent SMS sent to field team');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to create alert');
    } finally {
      setSendingSMS(false);
    }
  };

  const handleAddAlert = async () => {
    try {
      setError('');
      await complianceService?.createAlert({
        alertType: newAlert?.alertType,
        severity: newAlert?.severity,
        title: newAlert?.title,
        message: newAlert?.message,
        dueDate: newAlert?.dueDate || null
      });
      setShowAddForm(false);
      setNewAlert({
        alertType: 'license_expiry',
        severity: 'medium',
        title: '',
        message: '',
        dueDate: ''
      });
      await loadAlerts();
      
      // Show SMS notification indicator for critical/high alerts
      if (['critical', 'high']?.includes(newAlert?.severity)) {
        alert('Alert created and SMS notifications sent to field team members.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to create alert');
    }
  };

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case 'license_expiry': return 'Clock';
      case 'inspection_due': return 'FileSearch';
      case 'training_required': return 'Users';
      case 'document_missing': return 'FileX';
      case 'compliance_violation': return 'AlertTriangle';
      case 'audit_scheduled': return 'Calendar';
      default: return 'AlertCircle';
    }
  };

  const visibleAlerts = alerts?.filter(alert => !dismissedAlerts?.has(alert?.id));

  if (loading) {
    return (
      <div className="mb-6 bg-card rounded-lg border border-border p-6 agricultural-shadow">
        <div className="flex items-center justify-center py-4">
          <Icon name="Loader2" size={24} className="animate-spin text-primary mr-2" />
          <span className="text-muted-foreground">Loading alerts...</span>
        </div>
      </div>
    );
  }

  if (visibleAlerts?.length === 0 && !showAddForm) {
    return (
      <div className="bg-success/10 border border-success rounded-lg p-6 mb-6 agricultural-shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success rounded-lg">
              <Icon name="CheckCircle" size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-success">All Clear!</h3>
              <p className="text-sm text-success/80">No critical compliance alerts at this time.</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowAddForm(true)}
            iconName="Plus"
          >
            Add Alert
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground flex items-center space-x-2">
          <Icon name="AlertCircle" size={24} className="text-error" />
          <span>Critical Compliance Alerts</span>
        </h2>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} />
            <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Icon name="smartphone" className="w-3 h-3" />
              <span>SMS enabled for critical/high alerts</span>
            </div>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="text-sm"
            >
              <Icon name="plus" className="w-4 h-4 mr-1" />
              Add Alert
            </Button>
          </div>
        </div>
      </div>
      {error && (
        <div className="mb-4 bg-error/10 border border-error/20 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={20} className="text-error mr-3" />
            <span className="text-error">{error}</span>
          </div>
        </div>
      )}
      {/* Add Alert Form */}
      {showAddForm && (
        <div className="bg-card rounded-lg border border-border p-6 mb-6 agricultural-shadow">
          <h3 className="text-lg font-semibold text-foreground mb-4">Create New Alert</h3>
          <form onSubmit={handleCreateAlert} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Alert Type"
                options={alertTypeOptions}
                value={newAlert?.alertType}
                onChange={(value) => setNewAlert({ ...newAlert, alertType: value })}
              />
              <Select
                label="Severity"
                options={severityOptions}
                value={newAlert?.severity}
                onChange={(value) => setNewAlert({ ...newAlert, severity: value })}
              />
              <Input
                label="Title"
                value={newAlert?.title}
                onChange={(e) => setNewAlert({ ...newAlert, title: e?.target?.value })}
                placeholder="Enter alert title"
                required
              />
              <Input
                label="Due Date"
                type="date"
                value={newAlert?.dueDate}
                onChange={(e) => setNewAlert({ ...newAlert, dueDate: e?.target?.value })}
              />
            </div>
            <Input
              label="Message"
              value={newAlert?.message}
              onChange={(e) => setNewAlert({ ...newAlert, message: e?.target?.value })}
              placeholder="Enter alert message"
              required
            />
            <div className="flex space-x-2">
              <Button
                type="submit"
                className="w-full"
                disabled={sendingSMS}
              >
                {sendingSMS ? (
                  <>
                    <Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
                    Creating & Sending SMS...
                  </>
                ) : (
                  <>
                    <Icon name="Plus" className="w-4 h-4 mr-2" />
                    Create Alert
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {visibleAlerts?.map((alert) => {
          const config = getSeverityConfig(alert?.severity);
          const icon = getAlertIcon(alert?.alertType);
          
          return (
            <div
              key={alert?.id}
              className={`p-4 rounded-lg border-l-4 ${config?.bgColor} ${config?.borderColor} agricultural-shadow`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${config?.iconBg}`}>
                    <Icon name={icon} size={20} className={config?.iconColor} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-foreground">{alert?.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.bgColor} ${config?.textColor}`}>
                        {alert?.alertType?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {alert?.message}
                    </p>
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={14} className={config?.textColor} />
                        <span className={config?.textColor}>
                          Due: {formatDaysUntil(alert?.dueDate)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Zap" size={14} className="text-error" />
                        <span className="text-error font-medium">Action Required</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDismissAlert(alert?.id)}
                  className="text-muted-foreground hover:text-foreground smooth-transition"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm">
                    View Details
                  </Button>
                  <Button 
                    variant="default" 
                    size="sm"
                    onClick={() => handleResolveAlert(alert?.id)}
                  >
                    Resolve
                  </Button>
                </div>
                <span className={`text-xs font-medium uppercase tracking-wide ${config?.textColor}`}>
                  {alert?.severity}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CriticalAlertsPanel;